<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import ReferencesSlider from "../../../shared/components/Sliders/ReferencesSlider.svelte";
	import type { HomepageProps } from "./HomepageProps.js";
	import HomepageBanner from "../components/HomepageBanner.svelte";
	import Partners from "../../../shared/components/Partners.svelte";
	import HomepageServices from "../components/HomepageServices.svelte";
	import HomepageArticles from "../components/HomepageArticles.svelte";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import { getVertiNodeLevelDetailPath } from "../../../../core/schema/paths/getVertiNodeLevelDetailPath.js";
	import Link from "../../../shared/components/Link.svelte";
	import Card from "../../../shared/components/Card.svelte";
	import translations from "./Homepage.translations.json";
	import { vertiNodeLevelsPathMap } from "../../../../core/schema/paths/vertiNodeLevelsPathMap.js";
	import { getTranslate } from "../utils/getTranslate.js";
	import { VertiType } from "../../../../core/schema/VertiType.js";
	import { PartnerSet } from "../../../../core/schema/PartnerSet";
	import StaticPicture from "../../../shared/components/Picture/StaticPicture.svelte";
	import { vertiNodeAboutPathMap } from "../../../../core/schema/paths/vertiNodeAboutPathMap.js";
	import warehouseControlSm from "../assets/images/vertinode/warehouse-control.jpeg?webp&h=405&imagetools";
	import warehouseControlMd from "../assets/images/vertinode/warehouse-control.jpeg?webp&h=470&imagetools";
	import warehouseControlLg from "../assets/images/vertinode/warehouse-control.jpeg?webp&h=320&imagetools";
	import warehouseControlXl from "../assets/images/vertinode/warehouse-control.jpeg?webp&h=472&imagetools";
	import arrowRightLong from "../../../../core/assets/icons/arrow-right-long.svg?raw";

	const translate = getTranslate(translations);

	type $$Props = HomepageProps;
	const { partners, articles, references, vertiNodeLevels } = $$restProps as $$Props;
	const { TENANT_LOCALE } = getGlobalSettings();

	$: partnersSet = partners.filter(({ set }) => set === PartnerSet.Partners);
</script>

<Layout>
	<div class="container">
		<HomepageBanner />

		<section class="mb-12 lg:mb-24">
			<h2 class="text-center">{translate("headingPackages")}</h2>
			<div class="flex flex-col gap-2 md:grid md:grid-cols-2 xl:grid-cols-4">
				{#each vertiNodeLevels as level}
					<Card
						title={level.name[TENANT_LOCALE]}
						description={level.firstParagraph}
						urlSlug={getVertiNodeLevelDetailPath(level)[TENANT_LOCALE]}
						squaredImage
						customButtonText={translate("showMore")}
					/>
				{/each}
			</div>
			<Link class="mx-auto mt-8 text-center" asButton variant="primary" href="/{vertiNodeLevelsPathMap[TENANT_LOCALE]}">
				{translate("showAllFeatures")}
			</Link>
		</section>

		<div class="mb-4 flex flex-col gap-2 overflow-hidden lg:grid lg:grid-cols-2">
			<div class="order-2 grid place-items-center lg:order-1">
				<StaticPicture
					image={warehouseControlXl}
					alt={translate("warehouseControlImage")}
					height={472}
					width={708}
					loading="eager"
					class="relative aspect-[3/2] w-full"
					imgClass="rounded-lg inset-0 absolute"
					sourcesets={{
						"<md": warehouseControlSm,
						">md": warehouseControlMd,
						">lg": warehouseControlLg,
						">xl": warehouseControlXl,
					}}
				/>
			</div>
			<div class="order-1 flex flex-col gap-6 rounded-lg md:p-16 lg:order-2 lg:gap-2">
				<h2 class="font-spaceGrotesk mx-[initial] mt-6 pt-0 text-xl leading-[1] lg:mb-6 lg:mt-0">
					{translate("warehouseControl")}
				</h2>
				<p class="leading-6">{translate("warehouseControlDescription1")}</p>
				<p class="leading-6">{translate("warehouseControlDescription2")}</p>
				<div class="flex lg:mt-4">
					<Link asButton variant="primary" icon={arrowRightLong} href="/{vertiNodeAboutPathMap[TENANT_LOCALE]}">
						{translate("about")}
					</Link>
				</div>
			</div>
		</div>

		{#if partnersSet.length > 0}
			<section class="mb-12 lg:mb-24">
				<Partners partners={partnersSet} customTitle={translate("partners")} />
			</section>
		{/if}

		{#if references.length > 0}
			<section class="mb-12 lg:mb-24">
				<ReferencesSlider vertiType={VertiType.Node} {references} />
			</section>
		{/if}

		<section class="mb-12 lg:mb-24">
			<HomepageServices />
		</section>

		{#if articles.length > 0}
			<section class="mb-12 lg:mb-24">
				<HomepageArticles {articles} />
			</section>
		{/if}
	</div>
</Layout>
